(() => {
  if (customElements.get('avo-bama-external-dialog')) return;

  class AvoBamaExternalDialog extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.dialog = this.querySelector('[data-avo-bama-dialog]');
      if (!this.dialog) return;

      this.initialized = true;
      this.continueLink = this.dialog.querySelector('[data-avo-bama-dialog-continue]');
      this.closeButtons = this.dialog.querySelectorAll('[data-avo-bama-dialog-close]');
      this.defaultDestination = this.dialog.dataset.defaultDestination;
      this.root = document.documentElement;
      this.closeDuration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 150;
      this.activeTrigger = null;
      this.previousRootOverflow = '';
      this.previousRootPaddingRight = '';
      this.closeTimer = null;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleCancel = this.handleCancel.bind(this);
      this.requestClose = this.requestClose.bind(this);

      document.addEventListener('click', this.handleDocumentClick, true);
      this.dialog.addEventListener('click', this.handleDialogClick);
      this.dialog.addEventListener('cancel', this.handleCancel);
      this.closeButtons.forEach((button) => button.addEventListener('click', this.requestClose));
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      document.removeEventListener('click', this.handleDocumentClick, true);
      this.dialog.removeEventListener('click', this.handleDialogClick);
      this.dialog.removeEventListener('cancel', this.handleCancel);
      this.closeButtons.forEach((button) => button.removeEventListener('click', this.requestClose));
      window.clearTimeout(this.closeTimer);

      if (this.dialog.open) {
        this.dialog.close();
        this.unlockPage();
      }

      this.initialized = false;
    }

    lockPage() {
      const scrollbarWidth = Math.max(0, window.innerWidth - this.root.clientWidth);

      this.previousRootOverflow = this.root.style.overflow;
      this.previousRootPaddingRight = this.root.style.paddingRight;
      this.root.style.overflow = 'hidden';

      if (scrollbarWidth > 0) {
        this.root.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    unlockPage() {
      this.root.style.overflow = this.previousRootOverflow;
      this.root.style.paddingRight = this.previousRootPaddingRight;
    }

    requestClose() {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;

      this.dialog.classList.add('is-closing');
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        this.dialog.close();
        this.dialog.classList.remove('is-closing');
        this.unlockPage();
        this.activeTrigger?.focus({ preventScroll: true });
        this.activeTrigger = null;
      }, this.closeDuration);
    }

    getDestination(link) {
      try {
        const linkDestination = new URL(link.href, window.location.href);

        if (this.isYeaAlabamaUrl(linkDestination)) {
          return linkDestination.href;
        }
      } catch (error) {
        // Use the destination configured in the Theme Editor.
      }

      return this.defaultDestination;
    }

    isYeaAlabamaUrl(url) {
      return url.hostname === 'yea-alabama.com' || url.hostname.endsWith('.yea-alabama.com');
    }

    isAlabamaProductLink(link) {
      if (!link.matches('[data-avo-bama-product-link]')) return false;

      try {
        const destination = new URL(link.href, window.location.href);

        return this.isYeaAlabamaUrl(destination) && destination.pathname.includes('/products/');
      } catch (error) {
        return false;
      }
    }

    handleDocumentClick(event) {
      const productLink = event.target.closest?.('a[data-avo-bama-product-link]');

      if (!productLink || !this.isAlabamaProductLink(productLink)) return;

      event.preventDefault();

      if (typeof this.dialog.showModal !== 'function') {
        window.location.assign(this.getDestination(productLink));
        return;
      }

      this.activeTrigger = productLink;
      this.continueLink.href = this.getDestination(productLink);
      this.dialog.classList.remove('is-closing');

      if (!this.dialog.open) {
        this.lockPage();
        this.dialog.showModal();
      }
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.requestClose();
    }

    handleCancel(event) {
      event.preventDefault();
      this.requestClose();
    }
  }

  customElements.define('avo-bama-external-dialog', AvoBamaExternalDialog);
})();
